import React, { useState, useRef, useId, useEffect } from 'react';

/* Inline info-icon button with a hover/focus tooltip. Replaces verbose
 * "what is being computed" `<p>` notes that were eating vertical
 * space across the parcel-detail page.
 *
 * - Hover/focus opens the tooltip on desktop.
 * - Click/tap toggles on touch/keyboard.
 * - Tooltip text is `children`; falls back to `label` for the icon's
 *   aria-label so screen readers announce "More info: Fair Value".
 * - Uses the standard circled-"i" info glyph (matches lucide's `Info`
 *   icon) so the affordance reads as "additional information", not an
 *   eye (#929). Pure inline SVG so we don't pull in a new icon
 *   dependency (lucide-react is not installed in this repo).
 */
export default function InfoHint({
  children,
  label = 'More info',
  width = 280,
  align = 'left',
  inline = true,
}) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: 'relative',
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        verticalAlign: 'middle',
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          padding: 0,
          marginLeft: 6,
          background: 'transparent',
          border: 'none',
          color: '#9aa0a6',
          cursor: 'pointer',
          borderRadius: 999,
        }}
        data-testid="info-hint-button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>
      {open && (
        <span
          id={tipId}
          role="tooltip"
          data-testid="info-hint-tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            [align === 'right' ? 'right' : 'left']: 0,
            zIndex: 50,
            background: '#1f2933',
            color: '#fff',
            fontSize: '0.72rem',
            lineHeight: 1.4,
            padding: '8px 10px',
            borderRadius: 6,
            width,
            maxWidth: '90vw',
            boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
            whiteSpace: 'normal',
            pointerEvents: 'auto',
          }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
